import './manual-input-form.scss';
import {
    ChangeEvent,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { ZodFormattedError } from 'zod';
import { ClbFullPageFlow } from '@celebration/react/ClbFullPageFlow/ClbFullPageFlow';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { ClbTag } from '@celebration/react/ClbTag/ClbTag';
import { FooterButtons } from '@celebration/react/ClbFullPageFlow/ClbFullPageFlow.types';
import { ButtonPropsModal } from '@celebration/react/ClbModal/ClbModal.types';
import ManualInputFormService from './services/manual-input-form.service';
import IManualInputFormDataErrors from './components/manual-input-form-upload/interfaces/IManualInputFormDataErrors';
import IManualInputFormData from '../../../../../common/interfaces/IManualInputFormData';
import ManualInputFormUpload from './components/manual-input-form-upload/manual-input-form-upload';
import ManualInputFormSteps from './enums/steps.enum';
import IManualInputFormFullPageFlowProps from './interfaces/IManualInputFormFullPageFlowProps';
import LoadsImportResult from '../loads-import-result';
import LoadCreationResultTable from '../loads-creation-result-table';
import CsvImportService from '../../services/csv-import';
import IParseCsvReturn from '../../services/csv-import/interfaces/IParseCsvReturn';
import IParseResultTableData from '../loads-import-result/interfaces/IParseResultTableData';
import IValidationResult from '../../../../../common/interfaces/IValidationResult';
import ILoadCreationResultTableData from '../loads-import-result/interfaces/ILoadCreationResultTableData';

function ManualInputFormFullPageFlow({
    isManualInputFormOpened,
    setIsManualInputFormOpened,
}: Readonly<IManualInputFormFullPageFlowProps>) {
    const { t } = useTranslation();
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeStep, setActiveStep] = useState<ManualInputFormSteps>(
        ManualInputFormSteps.upload,
    );
    const [csvFile, setCsvFile] = useState<File | undefined>(undefined);
    const [parseResult, setParseResult] = useState<IParseCsvReturn>(
        {} as IParseCsvReturn,
    );
    const [loadCreationResult, setLoadCreationResult] = useState<
        ILoadCreationResultTableData[]
    >([]);

    enum InputStatus {
        Neutral,
        Sucess,
        Error,
    }

    const [typeFileColor, setTypeFileColor] = useState(InputStatus.Neutral);

    const emptyErrors = useMemo(() => {
        return {} as ZodFormattedError<IManualInputFormDataErrors>;
    }, []);

    const [errors, setErrors] = useState(emptyErrors);

    const closeModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
        setIsManualInputFormOpened(false);
        setActiveStep(ManualInputFormSteps.upload);
        setErrors(emptyErrors);
        setCsvFile(undefined);
        setParseResult({} as IParseCsvReturn);
        setLoadCreationResult([]);
        setTypeFileColor(InputStatus.Neutral);
    }, [setIsManualInputFormOpened, emptyErrors, InputStatus.Neutral]);

    const itemsModalFooter: [ButtonPropsModal, ButtonPropsModal] = [
        {
            role: 'button',
            styleType: 'primary',
            id: 'exit-button',
            label: t('Sair'),
            disabled: false,
            loading: false,
            title: t('Sair'),
            'aria-label': t('Sair'),
            'data-testid': 'modal-exit-button',
            onClick: closeModalHandler,
            key: 'modal-exit-button',
        },
        {
            role: 'button',
            styleType: 'secondary',
            id: 'back-button',
            label: t('Voltar'),
            disabled: false,
            loading: false,
            title: t('Voltar'),
            'aria-label': t('Voltar'),
            'data-testid': 'modal-back-button',
            onClick: () => {
                setIsExitModalOpen(false);
            },
            key: 'modal-back-button',
        },
    ];

    const fullPageFlowTitle = useMemo(() => {
        switch (activeStep) {
            case ManualInputFormSteps.upload:
                return `${t('ManualLoadImportEtapa1')}`;
            case ManualInputFormSteps.review:
                return `${t('ManualLoadImportEtapa2')}`;
            case ManualInputFormSteps.finish:
                return `${t('ManualLoadImportEtapa3')}`;
        }
    }, [activeStep, t]);

    const closeManualInputFormHandler = useCallback(() => {
        setIsExitModalOpen(true);
    }, []);

    const closeExitModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
    }, []);

    const setInputAsError = useCallback(
        (input: HTMLElement | null, label: HTMLElement) => {
            if (input != null) {
                input.style.setProperty(
                    'border-color',
                    'var(--color-feedback-danger-300)',
                );
                input.style.setProperty(
                    'background-color',
                    'var(--color-feedback-danger-100)',
                );
                input.style.setProperty('color', 'black');
                label.style.setProperty('color', 'black');
                input.style.setProperty(
                    '--labelBeforeBorderColor',
                    'var(--color-feedback-danger-300)',
                );
            }
        },
        [],
    );

    const setInputAsSucess = useCallback(
        (input: HTMLElement | null, label: HTMLElement) => {
            if (input != null) {
                input.style.setProperty(
                    'border-color',
                    'var(--color-feedback-positive-300)',
                );
                input.style.setProperty(
                    'background-color',
                    'var(--color-feedback-positive-100)',
                );
                input.style.setProperty('color', 'black');
                label.style.setProperty('color', 'black');
                input.style.setProperty(
                    '--labelBeforeBorderColor',
                    'var(--color-feedback-positive-300)',
                );
            }
        },
        [],
    );

    const setInputColor = useCallback((): void => {
        const input = document.getElementById('manual-input-upload-input');
        const label = document.querySelector(
            '.manual-input-upload-input .input-base-icon',
        ) as HTMLElement;

        switch (typeFileColor) {
            case InputStatus.Sucess: {
                setInputAsSucess(input, label);
                break;
            }
            case InputStatus.Error: {
                setInputAsError(input, label);
                break;
            }
            default: {
            }
        }
    }, [setInputAsSucess, setInputAsError, InputStatus, typeFileColor]);

    const validateInfos = useCallback(
        (manualInputData: IManualInputFormData) => {
            const infoResult: IValidationResult =
                ManualInputFormService.validateInfos(manualInputData);

            if (!infoResult?.success) {
                setErrors(
                    infoResult.errors as ZodFormattedError<IManualInputFormDataErrors>,
                );
                setTypeFileColor(InputStatus.Error);
                return false;
            }

            setTypeFileColor(InputStatus.Sucess);
            setErrors(emptyErrors);

            return true;
        },
        [emptyErrors, InputStatus],
    );

    const validateUploadFileFormStep = useCallback(
        async (manualInputData: IManualInputFormData) => {
            if (!validateInfos(manualInputData)) {
                return false;
            }

            setErrors(emptyErrors);

            return true;
        },
        [emptyErrors, validateInfos],
    );

    const handleResultsTableData = (): IParseResultTableData[] => {
        const tableData: IParseResultTableData[] = [];
        const errors = parseResult?.errors;

        if (errors?.length) {
            errors.forEach(error => {
                error.forEach(item => {
                    let lineNumber = 0;

                    if (item?.code !== 'MissingQuotes') {
                        lineNumber = item?.row ? item?.row + 2 : lineNumber;
                    }

                    tableData.push({
                        line: lineNumber,
                        status: tagError,
                        observation: item?.message,
                    });
                });
            });
        }

        return tableData;
    };

    const handleLoadsCreationResultTableData = useCallback(async () => {
        setIsLoading(true);

        const result = await CsvImportService.postLoad(parseResult.data, t);
        const errors = result?.errors;

        const tableData: ILoadCreationResultTableData[] = [];

        if (errors) {
            errors.forEach(error => {
                tableData.push({
                    loadId: error?.loadId,
                    observation: error?.error,
                });
            });
        }

        setLoadCreationResult(tableData);
        setIsLoading(false);
        setActiveStep(ManualInputFormSteps.finish);
    }, [parseResult, t]);

    const goToReviewStep = useCallback(() => {
        (async () => {
            setIsLoading(true);

            const validInfo = await validateUploadFileFormStep({
                fileName: csvFile?.name ?? '',
            });

            if (validInfo) {
                setParseResult(await CsvImportService.parseCsv(csvFile));
                setActiveStep(ManualInputFormSteps.review);
            } else {
                setTypeFileColor(InputStatus.Error);
            }

            setIsLoading(false);
        })();
    }, [validateUploadFileFormStep, csvFile, setTypeFileColor, InputStatus]);

    const goToFinishStep = useCallback(() => {
        (async () => {
            await handleLoadsCreationResultTableData();
        })();
    }, [handleLoadsCreationResultTableData]);

    const goToPreviousStep = useCallback(() => {
        setActiveStep(activeStep - 1);
    }, [activeStep]);

    const fullPageFlowButtonList: FooterButtons = useMemo(() => {
        const footerButtons: FooterButtons = [{}];

        if (activeStep === ManualInputFormSteps.upload) {
            footerButtons.push({
                id: 'close-button',
                label: t('Cancelar'),
                disabled: false,
                loading: false,
                title: t('Cancelar'),
                'aria-label': t('Cancelar'),
                styleType: 'secondary',
                onClick: closeManualInputFormHandler,
            });

            footerButtons.push({
                id: 'next-button',
                label: t('Avançar'),
                disabled: false,
                loading: false,
                title: t('TransportadoraAvancarCadastro'),
                'aria-label': t('TransportadoraAvancarCadastro'),
                styleType: 'primary',
                onClick: goToReviewStep,
            });
        }

        if (activeStep === ManualInputFormSteps.review) {
            footerButtons.push({
                id: 'back-button',
                label: t('Voltar'),
                disabled: false,
                loading: false,
                title: t('Voltar'),
                'aria-label': t('Voltar'),
                styleType: 'secondary',
                onClick: goToPreviousStep,
            });

            footerButtons.push({
                id: 'next-button',
                label: t('Confirmar'),
                disabled: false,
                loading: false,
                title: t('Confirmar'),
                'aria-label': t('Confirmar'),
                styleType: 'primary',
                onClick: goToFinishStep,
            });
        }

        if (activeStep === ManualInputFormSteps.finish) {
            footerButtons.push({
                id: 'next-button',
                label: t('Fechar'),
                disabled: false,
                loading: false,
                title: t('Fechar'),
                'aria-label': t('Fechar'),
                styleType: 'primary',
                onClick: closeModalHandler,
            });
        }

        return footerButtons;
    }, [
        activeStep,
        closeManualInputFormHandler,
        goToPreviousStep,
        goToReviewStep,
        goToFinishStep,
        closeModalHandler,
        t,
    ]);

    const renderExitConfirmationManualInputFormModal = () => {
        if (isExitModalOpen) {
            return (
                <ClbModal
                    title={t('ManualInputTituloModalCancelamento')}
                    open={isExitModalOpen}
                    onClose={closeExitModalHandler}
                    description={t('ManualInputTextoModalCancelamento')}
                    type="danger"
                    itemsFooter={itemsModalFooter}
                    key="manual-input-close-modal-confirmation"
                />
            );
        }
    };

    const setInputFileName = (fileName: string | undefined): void => {
        if (fileName !== undefined) {
            const input = document.getElementById('manual-input-upload-input');
            if (input != null) {
                input.style.setProperty(
                    '--labelBeforeContent',
                    `'${fileName}'`,
                );
                input.title = fileName;
            }
        }
    };

    const handleManualInputFormChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file: File | undefined = event?.target?.files?.[0];

            setCsvFile(file);

            validateInfos({
                fileName: file?.name ?? '',
            });

            setInputFileName(file?.name);
        },
        [validateInfos],
    );

    const handleLoadsNumbers = useCallback(() => {
        const totalLoadsProcessed: number = parseResult?.data?.length ?? 0;
        const totalLoadsNotProcessed: number = parseResult?.errors?.length ?? 0;
        const totalLoadsReceived: number =
            totalLoadsProcessed + totalLoadsNotProcessed;

        return {
            totalLoadsProcessed,
            totalLoadsNotProcessed,
            totalLoadsReceived,
        };
    }, [parseResult]);

    const tagError: ReactElement = <ClbTag type="negative">{t('Erro')}</ClbTag>;

    useEffect(() => {
        setInputColor();
        setInputFileName(csvFile?.name);
    }, [setInputColor, csvFile]);

    return (
        <>
            <ClbFullPageFlow
                title={fullPageFlowTitle}
                isOpen={isManualInputFormOpened}
                onClose={closeManualInputFormHandler}
                progressBar={{
                    customTag: '/2',
                    description: t('Etapas'),
                    initiallyAnimated: true,
                    maxValue: 2,
                    minValue: 0,
                    value: activeStep as number,
                }}
                footerButtons={fullPageFlowButtonList}
                data-testid="full-page-flow"
                key="full-page-flow"
            >
                {isLoading && (
                    <div
                        className="manual-input-form__loading"
                        data-testid="manual-input-form__loading"
                    >
                        <ClbLoading />
                    </div>
                )}

                {isManualInputFormOpened &&
                    activeStep === ManualInputFormSteps.upload &&
                    !isLoading && (
                        <ManualInputFormUpload
                            handleManualInputFormChange={
                                handleManualInputFormChange
                            }
                            inputErrors={errors}
                        />
                    )}

                {isManualInputFormOpened &&
                    activeStep === ManualInputFormSteps.review &&
                    !isLoading && (
                        <LoadsImportResult
                            loadsNumbers={{
                                loadsReceived:
                                    handleLoadsNumbers().totalLoadsReceived,
                                loadsProcessed:
                                    handleLoadsNumbers().totalLoadsProcessed,
                                loadsNotProcessed:
                                    handleLoadsNumbers().totalLoadsNotProcessed,
                            }}
                            loadsResult={handleResultsTableData()}
                        />
                    )}

                {isManualInputFormOpened &&
                    activeStep === ManualInputFormSteps.finish &&
                    !isLoading && (
                        <LoadCreationResultTable data={loadCreationResult} />
                    )}
            </ClbFullPageFlow>

            {renderExitConfirmationManualInputFormModal()}
        </>
    );
}

export default ManualInputFormFullPageFlow;
